import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterOutlet } from '@angular/router';
import { TegelModule } from '@scania/tegel-angular-17';
import { Building } from '../../core/models/building/building';
import { BuildingService } from '../../core/services/application/flexseat/building.service';
import { ReservationService } from '../../core/services/application/flexseat/reservation.service';
import { DateService } from '../../core/services/application/util/date/date.service';
import { MessageComponent } from '../../shared/message/message.component';

export interface ReservationForm {

    date: FormControl<string | null>;
    period: FormControl<string | null>;
    building: FormControl<number | null>;

}

@Component({
  selector: 'app-reservations',
  imports: [TegelModule, ReactiveFormsModule, RouterOutlet, MessageComponent],
  templateUrl: './reservations.component.html',
  styleUrl: './reservations.component.scss',
})
export class ReservationsComponent implements OnInit {

  private readonly router = inject(Router);
  private readonly reservationService = inject(ReservationService);
  private readonly buildingService = inject(BuildingService);
  private readonly dateService = inject(DateService);

  today: string | any = signal(this.formatDateToString(this.dateService.getCurrentDate()));
  todayPlus14: string | any = signal(this.getDatePlusDays(14));

  buildingData = signal<Building[]>(this.buildingService.buildingsData());

  constructor() {
    effect(() => {
      const buildings = this.buildingService.buildingsData();
      this.buildingData.set(buildings);
    });
  }

  bookingReqData = computed(() => this.reservationService.reservationReq());

  selectedBuilding = signal<number>(0);

  get isAdminContext(): boolean {
    return this.router.url.startsWith('/admin');
  }

  selectedBuildingData = computed(() => {

    const buildingId = this.selectedBuilding();

    if (!buildingId || buildingId <= 0) return null;

    return this.buildingData().find(item => item.id === buildingId) || null;

  });

  /** Uses computed() for messageData */
  messageData = computed(() => {
    const building = this.selectedBuildingData();

    if (!building) return null;
    if (building.statement_end) {
      const dataFim = new Date(building.statement_end);
      const hoje = new Date();
      if (dataFim <= hoje) return null;
    }

    return {
      type: 'information' as const,
      header: 'Information',
      content: building.statement || '',
    };
  });

  /**
   * Forms
   */
  reservationForm = new FormGroup<ReservationForm>({
    date: new FormControl<string | null>(this.today(), {
      nonNullable: true,
      validators: [Validators.required],
    }),
    period: new FormControl<string | null>('3', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    building: new FormControl<number | null>(this.selectedBuilding(), {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  /** Better to access form controls easily on forms */
  get controls() {
    return this.reservationForm.controls;
  }

  ngOnInit() {

    /** Subscribe to watch for changes in the form values */
    this.reservationForm.valueChanges.subscribe(values => {
      if (this.reservationForm.valid) {
        this.onSubmit();
      }
    });

    const urlSegments = this.router.url.split('/');
    const lastSegment = urlSegments[urlSegments.length - 1];

    if (lastSegment && lastSegment !== 'reservation') {
      const buildingId = Number(lastSegment);

      if (!isNaN(buildingId)) {
        this.selectedBuilding.set(buildingId);
        this.controls.building.setValue(buildingId);
      }
    }

  }

  /** Form submit */
  onSubmit() {

    if (this.reservationForm.invalid) {
      console.error('Form is invalid');
      return; // Stop submission if form is invalid
    }

    const formData = this.reservationForm.getRawValue();
    const buildingId = Number(formData.building) || 0;

    if (this.buildingData().map(item => item.id).includes(buildingId)) {
      this.messageData == null;
      this.selectedBuilding.set(buildingId);

      const reservationDate = formData.date!;
      const reservationPeriod = this.isAdminContext ? '3' : formData.period!;

      this.reservationService.setReservationData(
        reservationDate,
        formData.building!,
        reservationPeriod
      );

      const targetBase = this.isAdminContext ? '/admin' : '/reservation';
      this.router.navigate([targetBase, buildingId]);
    }

    /** Uses computed() for messageData */
    this.messageData = computed(() => {
      const building = this.selectedBuildingData();

      if (!building) return null;
      if (building.statement_end) {
        const dataFim = new Date(building.statement_end);
        const selectedDate = new Date(formData.date!);
        if (dataFim <= selectedDate) return null;
      }
      if (building.statement_start) {
        const dataInicio = new Date(building.statement_start);
        const selectedDate = new Date(formData.date!);
        if (dataInicio >= selectedDate) return null;
      }

      return {
        type: 'information' as const,
        header: 'Information',
        content: building.statement || '',
      };
    });
  }

  /**
   * Transform an date on an yyyy-MM-dd string
   * @param date Date value
   * @returns an string of Date value as yyyy-MM-dd
   */
  private formatDateToString(date: Date): string {
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Add days to an Date value
   * @param days days as int
   * @returns an string of Date value as yyyy-MM-dd
   */
  private getDatePlusDays(days: number): string {
    const date = this.dateService.getCurrentDate();
    date.setDate(date.getDate() + days);
    return this.formatDateToString(date);
  }

}